import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getCurrentSession, buildLoginRedirectUrl, invalidateSessionCache } from './auth';
import { signUserInfo, extractUserInfoFromSession } from '../utils/jwt';

const baseURL = process.env.REACT_APP_KFC_API || 'http://localhost:30080/api/kfc';
// JWT密钥，用于签名用户信息（应该由auth服务颁发给kfc-web服务）
const JWT_SECRET = process.env.REACT_APP_JWT_SECRET || 'kfc-web-secret-key-change-in-production';

export const kfcApi = axios.create({ baseURL, timeout: 10000 });

// 请求拦截器：校验会话并添加用户信息签名
kfcApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let session = null;
    try {
      session = await getCurrentSession();
    } catch (error) {
      // whoami 发生网络/CORS异常时，不跳转登录，避免 30090 <-> 3001 循环。
      return Promise.reject(error);
    }

    if (!session) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[KFC API] 无会话，将跳转登录。请先通过 Kratos 登录后再访问。');
      }
      window.location.href = buildLoginRedirectUrl();
      return Promise.reject(new Error('未登录，正在跳转到登录页面'));
    }

    try {
      const userInfo = extractUserInfoFromSession(session);
      const signedToken = await signUserInfo(userInfo, JWT_SECRET);
      config.headers['X-User-Info'] = signedToken;
      return config;
    } catch (error: any) {
      // 有 session 但提取/签名失败时不跳转登录，仅让本次请求失败
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
);

// 响应拦截器：仅当会话确实失效（whoami 无会话）时才跳转登录，避免 KFC 后端 401 误判为未登录
kfcApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      let session = null;
      try {
        session = await getCurrentSession();
      } catch {
        // whoami 非 401 场景（如 CORS/网络）不触发重定向，避免循环跳转
        return Promise.reject(error);
      }
      if (!session) {
        invalidateSessionCache();
        window.location.href = buildLoginRedirectUrl();
      }
    }
    return Promise.reject(error);
  }
);

/** 与后端 AgentBasicInfo / collection 结构一致 */
export interface AgentDTO {
  id?: string;
  basicInfo?: {
    name?: string;
    identifier?: string;
  };
  contact?: {
    contactPerson?: { name?: string; phone?: string };
    address?: string;
  };
  officialWebsite?: {
    url?: string;
    username?: string;
    password?: string;
  };
  apiConfig?: {
    priceEndPoint?: string;
    trackEndPoint?: string;
    logisticsSystemEndPoint?: string;
    appKey?: string;
    appSecret?: string;
    coid?: string;
    nonce?: string;
    version?: string;
  };
  bankAccount?: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
  };
  /** 代理能力映射：能力名称 -> 对应 API 名称 */
  capabilities?: { capability?: string; apiName?: string }[];
  createdAt?: string;
}

export interface ChannelDTO {
  id: string;
  agentId: string;
  channelCode: string;
  channelName?: string;
  volumeCoefficient?: number;
  versionTimestamp?: string;
}

export interface TransportModeDTO {
  id: string;
  mode: string;
  displayName?: string;
  carrierIds?: string[];
  isActive?: boolean;
  createdAt?: string;
}

export interface CarrierDTO {
  id: string;
  carrierCode: string;
  carrierName?: string;
  remark?: string;
  isActive?: boolean;
  createdAt?: string;
}

/** 起运地/目的地：国家名称、国家编码(ISO 3166-1 alpha-2)、城市名称 */
export interface RouteLocationDTO {
  countryName?: string;
  countryCode?: string;
  cityName?: string;
}

export interface RouteDTO {
  id: string;
  routeCode: string;
  /** D2D（专线）/ P2P（港到港） */
  serviceType?: 'D2D' | 'P2P' | string;
  origin?: RouteLocationDTO;
  destination?: RouteLocationDTO;
  transitTime?: string;
  transportMode?: string;
  /** 承运商 ID */
  carrierId?: string;
  carrierCode?: string;
  carrierName?: string;
  customsProps?: string;
  priceCoefficient?: number;
  isActive?: boolean;
  channelIds?: string[];
}

export interface ReceivingRecordDTO {
  id: string;
  trackingNumber: string;
  source?: string;
  supplier?: string;
  preDeclarationId?: string;
  shipmentId?: string;
  measurementJson?: string;
  trackingDetailsJson?: string;
  createdAt?: string;
}

export interface PreDeclarationDTO {
  id: string;
  preDeclarationCode: string;
  clientCode: string;
  clientAddr?: string;
  routeId: string;
  status: string;
  expectedShipDate?: string;
  cargoDescJson?: string;
  customsDeclJson?: string;
  shipmentId?: string;
  createdBy?: string;
  createdAt?: string;
  receivingRecords?: ReceivingRecordDTO[];
}

export interface ShipmentInvoiceDTO {
  id: string;
  shipmentId: string;
  fileName: string;
  generatedAt: string;
}

export interface ShipmentDTO {
  shipmentId: string;
  /** 预申报单 ID，用于绑定货物（仅预申报状态时有值） */
  preDeclarationId?: string;
  clientCode?: string;
  clientAddr?: string;
  routeId?: string;
  routeName?: string;
  /** 目的地国家（只读：线路 destination） */
  destinationCountry?: string;
  /** 代理标识（只读：由线路→首渠道→代理解析） */
  agentIdentifier?: string;
  /** 服务类型：D2D / P2P */
  serviceType?: string;
  /** 运输方式：AIR / SEA / EXPRESS / … */
  transportMode?: string;
  /** 业务渠道 ID */
  channelId?: string;
  channelCode?: string;
  channelName?: string;
  channelShipmentId?: string;
  /** 代理入库单号（交代理仓后由代理 API 返回） */
  agentInboundNo?: string;
  /** 代理订单来源：CREATED_IN_KFY / SYNCED_FROM_AGENT */
  agentOrderOrigin?: string;
  /** 代理入库状态（0新增,1已下单,2收货中,3已收货,5已确认,7调拨中,8扣货等） */
  agentInboundStatus?: number;
  /** 代理出库状态（0待出库,1出库中,2已出库） */
  agentOutboundStatus?: number;
  /** 代理测量数据（JSON 字符串） */
  agentMeasurementDataJson?: string;
  /** 代理测量重量（毛重，kg） */
  agentMeasuredWeightKg?: number;
  /** 代理测量体积（m³） */
  agentMeasuredVolumeCbm?: number;
  /** 箱数不一致待处理：详情「绑定的货物」区展示三选一 */
  cargoSyncPending?: boolean;
  cargoSyncKfyCount?: number;
  cargoSyncAgentCount?: number;
  /** 最新轨迹展示（物流格式、按时间排序） */
  agentTrackingLatest?: { time?: string; location?: string; description?: string }[];
  status: string;
  expectedShipDate?: string;
  cargoDescJson?: string;
  customsDeclJson?: string;
  confirmedWeight?: number;
  pendingWeight?: number;
  /** 绑定货物箱数（服务端聚合） */
  cartonCount?: number;
  /** 运单备注（可选） */
  remark?: string;
  createdAt?: string;
  updatedAt?: string;
  /** 进入当前状态时刻 */
  statusChangedAt?: string;
  /** 首次签收时刻 */
  deliveredAt?: string;
  receivingRecords?: ReceivingRecordDTO[];
}

export const agentApi = {
  list: () => kfcApi.get('/agents') as Promise<AxiosResponse<AgentDTO[]>>,
  get: (id: string) => kfcApi.get(`/agents/${id}`) as Promise<AxiosResponse<AgentDTO>>,
  create: (dto: Partial<AgentDTO>) => kfcApi.post('/agents', dto) as Promise<AxiosResponse<AgentDTO>>,
  update: (id: string, dto: Partial<AgentDTO>) => kfcApi.put(`/agents/${id}`, dto) as Promise<AxiosResponse<AgentDTO>>,
  delete: (id: string) => kfcApi.delete(`/agents/${id}`),
  testConnection: (id: string) => kfcApi.post(`/agents/${id}/test-connection`) as Promise<AxiosResponse<{ success: boolean }>>,
};

export const channelApi = {
  list: (agentId?: string) => kfcApi.get('/channels', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<ChannelDTO[]>>,
  get: (id: string) => kfcApi.get(`/channels/${id}`) as Promise<AxiosResponse<ChannelDTO>>,
  save: (dto: Partial<ChannelDTO>) => kfcApi.post('/channels', dto) as Promise<AxiosResponse<ChannelDTO>>,
  update: (id: string, dto: Partial<ChannelDTO>) => kfcApi.put(`/channels/${id}`, dto) as Promise<AxiosResponse<ChannelDTO>>,
  delete: (id: string) => kfcApi.delete(`/channels/${id}`),
};

export const transportModeApi = {
  list: (activeOnly?: boolean) =>
    kfcApi.get('/transport-modes', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<
      AxiosResponse<TransportModeDTO[]>
    >,
  get: (id: string) => kfcApi.get(`/transport-modes/${id}`) as Promise<AxiosResponse<TransportModeDTO>>,
  create: (dto: Partial<TransportModeDTO>) =>
    kfcApi.post('/transport-modes', dto) as Promise<AxiosResponse<TransportModeDTO>>,
  update: (id: string, dto: Partial<TransportModeDTO>) =>
    kfcApi.put(`/transport-modes/${id}`, dto) as Promise<AxiosResponse<TransportModeDTO>>,
  delete: (id: string) => kfcApi.delete(`/transport-modes/${id}`),
};

export const carrierApi = {
  list: (activeOnly?: boolean) =>
    kfcApi.get('/carriers', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<
      AxiosResponse<CarrierDTO[]>
    >,
  get: (id: string) => kfcApi.get(`/carriers/${id}`) as Promise<AxiosResponse<CarrierDTO>>,
  create: (dto: Partial<CarrierDTO>) =>
    kfcApi.post('/carriers', dto) as Promise<AxiosResponse<CarrierDTO>>,
  update: (id: string, dto: Partial<CarrierDTO>) =>
    kfcApi.put(`/carriers/${id}`, dto) as Promise<AxiosResponse<CarrierDTO>>,
  delete: (id: string) => kfcApi.delete(`/carriers/${id}`),
};

export const routeApi = {
  list: (activeOnly?: boolean) => kfcApi.get('/routes', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<AxiosResponse<RouteDTO[]>>,
  get: (id: string) => kfcApi.get(`/routes/${id}`) as Promise<AxiosResponse<RouteDTO>>,
  create: (dto: Partial<RouteDTO>) => kfcApi.post('/routes', dto) as Promise<AxiosResponse<RouteDTO>>,
  update: (id: string, dto: Partial<RouteDTO>) => kfcApi.put(`/routes/${id}`, dto) as Promise<AxiosResponse<RouteDTO>>,
};

export const preDeclarationApi = {
  list: () => kfcApi.get('/pre-declarations') as Promise<AxiosResponse<PreDeclarationDTO[]>>,
  get: (id: string) => kfcApi.get(`/pre-declarations/${id}`) as Promise<AxiosResponse<PreDeclarationDTO>>,
  create: (dto: {
    clientCode: string;
    clientAddr?: string;
    serviceType: string;
    transportMode: string;
    channelId?: string;
    routeCode?: string;
    shipmentId?: string;
    expectedShipDate?: string;
    cargoDescJson: string;
    customsDeclJson?: string;
    remark?: string;
  }) => kfcApi.post('/pre-declarations', dto) as Promise<AxiosResponse<PreDeclarationDTO>>,
  bindReceiving: (id: string, receivingRecordIds: string[]) =>
    kfcApi.post(`/pre-declarations/${id}/bind-receiving`, { preDeclarationId: id, receivingRecordIds }),
};

export const shipmentApi = {
  list: (params?: { clientCode?: string; status?: string; shipmentIdKeyword?: string; page?: number; size?: number }) =>
    kfcApi.get('/shipments', { params: params || {} }) as Promise<AxiosResponse<{ content: ShipmentDTO[]; totalElements: number }>>,
  get: (shipmentId: string) => kfcApi.get(`/shipments/${shipmentId}`) as Promise<AxiosResponse<ShipmentDTO>>,
  handoverConfirm: (dto: { shipmentId: string; pendingWeight?: number; handoverProofJson?: string; deliveryTrackingNo?: string }) =>
    kfcApi.post('/shipments/handover', dto),
  weightConfirm: (dto: { shipmentId: string; confirmedWeight?: number; accept?: boolean }) =>
    kfcApi.post('/shipments/weight-confirm', dto),
  setChannelShipmentId: (shipmentId: string, channelShipmentId: string) =>
    kfcApi.put(`/shipments/${shipmentId}/channel-shipment-id`, { channelShipmentId }),
  updateRoute: (shipmentId: string, routeCode: string) =>
    kfcApi.put(`/shipments/${shipmentId}/route`, { routeCode }),
  updateLogistics: (
    shipmentId: string,
    body: { serviceType: string; transportMode: string; channelId?: string | null; routeCode?: string | null }
  ) => kfcApi.put(`/shipments/${shipmentId}/logistics`, body),
  updateRemark: (shipmentId: string, remark: string) =>
    kfcApi.put(`/shipments/${shipmentId}/remark`, { remark }),
  getSubmitDepotContext: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/submit-depot-context`) as Promise<AxiosResponse<SubmitDepotContextDTO>>,
  checkAgentOrder: (shipmentId: string, params?: { agentInboundNo?: string }) =>
    kfcApi.get(`/shipments/${shipmentId}/check-agent-order`, {
      params: params?.agentInboundNo ? { agentInboundNo: params.agentInboundNo } : undefined,
    }) as Promise<AxiosResponse<CheckAgentOrderResultDTO>>,
  submitToAgentDepot: (shipmentId: string, body?: SubmitToDepotRequest) =>
    kfcApi.post(`/shipments/${shipmentId}/submit-depot`, body || {}) as Promise<AxiosResponse<SubmitToDepotResultDTO>>,
  getAgentLabel: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/agent-label`) as Promise<AxiosResponse<AgentLabelResult>>,
  cancelAgentOrder: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/cancel-agent-order`) as Promise<AxiosResponse<CancelOrderResult>>,
  submitInvoice: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/submit-invoice`),
  /** 生成发票 Excel，返回发票记录 */
  generateInvoice: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/generate-invoice`) as Promise<AxiosResponse<ShipmentInvoiceDTO>>,
  /** 运单下的发票记录列表 */
  listInvoices: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/invoices`) as Promise<AxiosResponse<ShipmentInvoiceDTO[]>>,
  /** 下载发票文件（返回 blob） */
  downloadInvoice: (shipmentId: string, invoiceId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/invoices/${invoiceId}/download`, { responseType: 'blob' }) as Promise<AxiosResponse<Blob>>,
  /** 同步轨迹（拉代理轨迹并按推断前向推进状态） */
  syncTracking: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-tracking`) as Promise<AxiosResponse<SyncTrackingDataResultDTO>>,
  /** 同步代理入仓数据（入库/出库/测量，仅当有代理入库单号时可操作） */
  syncAgentData: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-agent-data`) as Promise<AxiosResponse<SyncAgentDataResultDTO>>,
  /** 详情区处理绑定箱数与代理测量箱数不一致 */
  resolveCargoSync: (shipmentId: string, body: { mode: 'REPLACE' | 'MERGE' | 'LATER' }) =>
    kfcApi.post(`/shipments/${shipmentId}/resolve-cargo-sync`, body) as Promise<AxiosResponse<ResolveCargoSyncResultDTO>>,
  /** 同步轨迹（与 syncTracking 同义，兼容旧路径） */
  syncTrackingData: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-tracking-data`) as Promise<AxiosResponse<SyncTrackingDataResultDTO>>,
  /** 获取轨迹展示数据（代理原始或兼容结构），用于时间线等富展示 */
  getTrackingDisplay: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/tracking-display`) as Promise<AxiosResponse<TrackingDisplayDTO>>,
};

/** 轨迹展示数据结构（兼容不同代理：56yorky 等含 trackInfoList/inputChanges） */
export interface TrackingDisplayDTO {
  serviceName?: string;
  userNo?: string;
  latestTime?: string;
  latestStatus?: string;
  latestDetail?: string;
  trackInfoList?: TrackInfoItemDTO[];
  inputChanges?: InputChangeItemDTO[];
}

export interface TrackInfoItemDTO {
  trackTime?: string;
  trackStation?: string;
  trackStationEn?: string;
  trackDesc?: string;
  trackDescEn?: string;
  trackStatusText?: string;
  [key: string]: unknown;
}

export interface InputChangeItemDTO {
  createTime?: string;
  busiCode?: string;
  remark?: string;
  [key: string]: unknown;
}

export const receivingRecordApi = {
  listUnbound: () => kfcApi.get('/receiving-records/unbound') as Promise<AxiosResponse<ReceivingRecordDTO[]>>,
  listByPreDeclaration: (preDeclarationId: string) =>
    kfcApi.get(`/receiving-records/by-pre-declaration/${preDeclarationId}`) as Promise<AxiosResponse<ReceivingRecordDTO[]>>,
  create: (dto: { trackingNumber: string; source?: string; supplier?: string; measurementJson?: string }) =>
    kfcApi.post('/receiving-records', dto) as Promise<AxiosResponse<ReceivingRecordDTO>>,
};

export const channelSyncApi = {
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/channel-sync/agents/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; channels: ChannelDTO[] }>>,
};

export interface AgentDepotDTO {
  id?: string;
  agentId?: string;
  depotId?: string;
  depotName?: string;
  depotAddr?: string;
  depotContact?: string;
  /** AUTO=代理API同步；MANUAL=手动添加 */
  source?: 'AUTO' | 'MANUAL' | string;
}

/** 交代理仓请求：选择仓库、货拉拉单号、照片等；或同步代理已有运单 */
export interface SubmitToDepotRequest {
  depotId?: string;
  huolalaOrderNo?: string;
  photoUrls?: string[];
  /** 为 true 时同步代理已有运单，不 createOrder */
  syncExisting?: boolean;
  /** 查询未命中时仍强制用运单号绑定（哲盟手动号） */
  forceBind?: boolean;
  /** 哲盟 Q5 创建订单重量 kg */
  weig?: number;
  /** 哲盟 Q5 服务渠道编码 hubInCode */
  hubInCode?: string;
  /** 同步已有运单时是否创建货物，默认 true */
  syncCargo?: boolean;
  /** 勾选同步的货物 sourceIndex；省略且 syncCargo=true 表示全选 */
  selectedCargoIndexes?: number[];
  /** track 未命中时用入仓单号经 depot/info 校验后绑定（56yorky） */
  agentInboundNo?: string;
}

/** 代理货物预览项 */
export interface AgentCargoItemDTO {
  sourceIndex?: number;
  cartonId?: string;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  measuredWeightKg?: number;
  billingWeightKg?: number;
  volumeCbm?: number;
  locationCode?: string;
}

/** 同步代理入仓数据结果 */
export interface SyncAgentDataResultDTO {
  statusSynced?: boolean;
  cargoSyncPending?: boolean;
  kfyCartonCount?: number;
  agentCargoCount?: number;
  message?: string;
  cargoItems?: AgentCargoItemDTO[];
}

/** 同步轨迹结果（拉轨迹 + 可选状态前向推进） */
export interface SyncTrackingDataResultDTO {
  trackingUpdated?: boolean;
  statusAdvanced?: boolean;
  previousStatus?: string;
  newStatus?: string;
  inferredStatus?: string;
  message?: string;
}

export interface ResolveCargoSyncResultDTO {
  success?: boolean;
  message?: string;
  mode?: string;
  createdCartons?: string[];
  unboundCartons?: string[];
  reboundCartons?: string[];
  warnings?: string[];
  cargoSyncPending?: boolean;
}

export interface SubmitToDepotResultDTO {
  success?: boolean;
  message?: string;
  agentInboundNo?: string;
  agentOrderOrigin?: string;
  createdCartons?: string[];
  warnings?: string[];
}

export interface AgentLabelResult {
  success?: boolean;
  message?: string;
  labelUrl?: string;
  invoiceUrl?: string;
  agentInboundNo?: string;
}

export interface CancelOrderResult {
  success?: boolean;
  message?: string;
}

/** 交代理仓前：查代理是否已有运单及对比信息 */
export interface CheckAgentOrderResultDTO {
  exists: boolean;
  agentInboundNo?: string;
  message?: string;
  agentIdentifier?: string;
  kfy?: {
    shipmentId?: string;
    clientCode?: string;
    routeName?: string;
    cartonCount?: number;
    status?: string;
  };
  agent?: {
    userNo?: string;
    inputId?: string;
    serviceName?: string;
    trackSummary?: string;
    raw?: unknown;
  };
  cargoItems?: AgentCargoItemDTO[];
  cargoFetchMessage?: string;
  /** 56yorky：需手动填入仓单号再校验 */
  needManualInboundNo?: boolean;
}

/** 交代理仓弹窗上下文：代理信息 + 代理仓库列表 */
export interface SubmitDepotContextDTO {
  agentId: string;
  agent: AgentDTO | null;
  depots: AgentDepotDTO[];
  serviceChannels?: AgentServiceChannelDTO[];
  suggestedWeig?: number;
  weigRequiredForCreate?: boolean;
}

export interface AgentServiceChannelDTO {
  id?: string;
  agentId?: string;
  serviceId?: number;
  serviceName?: string;
  /** 外部渠道编码（哲盟 Q5 hubInId/code） */
  serviceCode?: string;
  serviceStatus?: number;
  country?: string;
  stype?: string;
}

export const agentDepotApi = {
  list: (agentId?: string) =>
    kfcApi.get('/agent-depots', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<AgentDepotDTO[]>>,
  create: (body: AgentDepotDTO) =>
    kfcApi.post('/agent-depots', body) as Promise<AxiosResponse<AgentDepotDTO>>,
  remove: (id: string) => kfcApi.delete(`/agent-depots/${id}`),
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/agent-depots/sync/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; depots: AgentDepotDTO[] }>>,
};

export const agentServiceApi = {
  list: (agentId?: string) =>
    kfcApi.get('/agent-services', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<AgentServiceChannelDTO[]>>,
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/agent-services/sync/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; services: AgentServiceChannelDTO[] }>>,
};

export interface CustomerAddressDTO {
  id?: string;
  label?: string;
  remark?: string;
  contactName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: string;
  postalCode?: string;
}

/** 运单/预申报选址摘要（不含敏感字段） */
export interface CustomerAddressSummaryDTO {
  id?: string;
  label?: string;
  remark?: string;
}

export interface CustomerAccountDTO {
  id?: string;
  transactionJson?: string;
  payableAmount?: number;
  paidAmount?: number;
}

export interface CustomerDTO {
  id?: string;
  customerCode: string;
  domain?: string;
  name: string;
  email?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
  addresses?: CustomerAddressDTO[];
  account?: CustomerAccountDTO;
}

export interface CustomerImportSummaryDTO {
  create: number;
  appendAddress: number;
  unchanged: number;
  error: number;
}

export interface CustomerImportAppendItemDTO {
  customerId?: string;
  customerCode?: string;
  name?: string;
  addresses?: CustomerAddressDTO[];
}

export interface CustomerImportErrorDTO {
  row: number;
  message: string;
}

export interface CustomerImportPreviewDTO {
  summary: CustomerImportSummaryDTO;
  toCreate: CustomerDTO[];
  toAppendAddress: CustomerImportAppendItemDTO[];
  errors: CustomerImportErrorDTO[];
}

export interface CustomerImportConfirmResultDTO {
  created: number;
  appendedAddress: number;
}

export const customerApi = {
  list: () => kfcApi.get('/customers') as Promise<AxiosResponse<CustomerDTO[]>>,
  get: (id: string) => kfcApi.get(`/customers/${id}`) as Promise<AxiosResponse<CustomerDTO>>,
  getByCode: (customerCode: string) =>
    kfcApi.get(`/customers/by-code/${encodeURIComponent(customerCode)}`) as Promise<AxiosResponse<CustomerDTO>>,
  listAddressSummaries: (customerCode: string) =>
    kfcApi.get(`/customers/by-code/${encodeURIComponent(customerCode)}/addresses`) as Promise<
      AxiosResponse<CustomerAddressSummaryDTO[]>
    >,
  getAddressDetail: (id: string) =>
    kfcApi.get(`/customers/addresses/${encodeURIComponent(id)}`) as Promise<AxiosResponse<CustomerAddressDTO>>,
  create: (dto: Partial<CustomerDTO>) => kfcApi.post('/customers', dto) as Promise<AxiosResponse<CustomerDTO>>,
  update: (id: string, dto: Partial<CustomerDTO>) =>
    kfcApi.put(`/customers/${id}`, dto) as Promise<AxiosResponse<CustomerDTO>>,
  delete: (id: string) => kfcApi.delete(`/customers/${id}`),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/customers/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<CustomerImportPreviewDTO>>;
  },
  importConfirm: (body: {
    toCreate: CustomerDTO[];
    toAppendAddress: CustomerImportAppendItemDTO[];
  }) =>
    kfcApi.post('/customers/import/confirm', body, {
      timeout: 120000,
    }) as Promise<AxiosResponse<CustomerImportConfirmResultDTO>>,
  downloadTemplate: () =>
    kfcApi.get('/customers/import/template', { responseType: 'blob', timeout: 30000 }),
};

/** 供应商联系人 */
export interface SupplierContactDTO {
  id?: string;
  contactName?: string;
  phoneNumber?: string;
  remark?: string;
}

/** 供应商（履行中心供应商管理） */
export interface SupplierDTO {
  id?: string;
  supplierId?: string;
  supplierName?: string;
  address?: string;
  contacts?: SupplierContactDTO[];
  createdAt?: string;
}

export interface SupplierImportAppendItemDTO {
  id?: string;
  supplierId?: string;
  supplierName?: string;
  contacts?: SupplierContactDTO[];
}

export interface SupplierImportSummaryDTO {
  create: number;
  appendContact: number;
  unchanged: number;
  error: number;
}

export interface SupplierImportErrorDTO {
  row: number;
  message: string;
}

export interface SupplierImportPreviewDTO {
  summary: SupplierImportSummaryDTO;
  toCreate: SupplierDTO[];
  toAppendContact: SupplierImportAppendItemDTO[];
  errors: SupplierImportErrorDTO[];
}

export interface SupplierImportConfirmResultDTO {
  created: number;
  appendedContact: number;
}

export const supplierApi = {
  list: () => kfcApi.get('/suppliers') as Promise<AxiosResponse<SupplierDTO[]>>,
  get: (id: string) => kfcApi.get(`/suppliers/${id}`) as Promise<AxiosResponse<SupplierDTO>>,
  getByCode: (supplierId: string) =>
    kfcApi.get(`/suppliers/by-code/${encodeURIComponent(supplierId)}`) as Promise<AxiosResponse<SupplierDTO>>,
  create: (dto: Partial<SupplierDTO>) => kfcApi.post('/suppliers', dto) as Promise<AxiosResponse<SupplierDTO>>,
  update: (id: string, dto: Partial<SupplierDTO>) =>
    kfcApi.put(`/suppliers/${id}`, dto) as Promise<AxiosResponse<SupplierDTO>>,
  delete: (id: string) => kfcApi.delete(`/suppliers/${id}`),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/suppliers/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<SupplierImportPreviewDTO>>;
  },
  importConfirm: (body: { toCreate: SupplierDTO[]; toAppendContact?: SupplierImportAppendItemDTO[] }) =>
    kfcApi.post('/suppliers/import/confirm', body, { timeout: 120000 }) as Promise<
      AxiosResponse<SupplierImportConfirmResultDTO>
    >,
  downloadTemplate: () =>
    kfcApi.get('/suppliers/import/template', { responseType: 'blob', timeout: 30000 }),
};

/** 产品（设计文档 Product：海关编码、名称、品牌、图片、用途、材质、价格） */
export interface ProductDTO {
  id?: string;
  hscode?: string;
  nameZN?: string;
  nameEN?: string;
  brand?: string;
  model?: string;
  picture?: string;
  containsBattery?: string;
  containsMagnet?: string;
  usage?: { zh_CN?: string; en_US?: string };
  material?: { zh_CN?: string; en_US?: string };
  price?: { USD?: number; RMB?: number };
}

export interface ProductImportSummaryDTO {
  create: number;
  update: number;
  unchanged: number;
  error: number;
}

export interface ProductImportUpdateItemDTO {
  before: ProductDTO;
  after: ProductDTO;
}

export interface ProductImportErrorDTO {
  row: number;
  message: string;
}

export interface ProductImportPreviewDTO {
  summary: ProductImportSummaryDTO;
  toCreate: ProductDTO[];
  toUpdate: ProductImportUpdateItemDTO[];
  errors: ProductImportErrorDTO[];
}

export interface ProductImportConfirmResultDTO {
  created: number;
  updated: number;
}

export function productKey(nameEN?: string, hscode?: string): string {
  return `${(nameEN || '').trim()}::${(hscode || '').trim()}`;
}

export function parseProductKey(key: string): { nameEN: string; hscode: string } {
  const idx = key.indexOf('::');
  if (idx < 0) return { nameEN: '', hscode: key.trim() };
  return { nameEN: key.slice(0, idx).trim(), hscode: key.slice(idx + 2).trim() };
}

export const productApi = {
  list: (params?: { view?: 'summary' | string }) =>
    kfcApi.get('/products', { params: params ?? {} }) as Promise<AxiosResponse<ProductDTO[]>>,
  get: (id: string) => kfcApi.get(`/products/${id}`) as Promise<AxiosResponse<ProductDTO>>,
  getByHscode: (hscode: string) =>
    kfcApi.get(`/products/by-hscode/${encodeURIComponent(hscode)}`) as Promise<AxiosResponse<ProductDTO>>,
  getByKey: (nameEN: string, hscode: string) =>
    kfcApi.get('/products/by-key', { params: { nameEN, hscode } }) as Promise<AxiosResponse<ProductDTO>>,
  create: (dto: Partial<ProductDTO>) => kfcApi.post('/products', dto) as Promise<AxiosResponse<ProductDTO>>,
  update: (id: string, dto: Partial<ProductDTO>) =>
    kfcApi.put(`/products/${id}`, dto) as Promise<AxiosResponse<ProductDTO>>,
  delete: (id: string) => kfcApi.delete(`/products/${id}`),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return kfcApi.post('/products/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }) as Promise<AxiosResponse<ProductImportPreviewDTO>>;
  },
  importConfirm: (body: { toCreate: ProductDTO[]; toUpdate: ProductDTO[] }) =>
    kfcApi.post('/products/import/confirm', body, { timeout: 120000 }) as Promise<AxiosResponse<ProductImportConfirmResultDTO>>,
  downloadTemplate: () =>
    kfcApi.get('/products/import/template', { responseType: 'blob', timeout: 30000 }),
};

/** 货物（设计文档 Carton） */
export interface CartonProductItemDTO {
  hscode?: string;
  nameEN?: string;
  amount?: number;
}

export interface CartonDTO {
  id?: string;
  cartonId?: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
  state?: string;
  states?: Record<string, Record<string, unknown>>;
  shipmentId?: string;
  batchId?: string;
  batchRemark?: string;
  measuredWeightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  billingWeightKg?: number;
  createTimestamp?: number;
}

export interface CreateCartonRequest {
  cartonId: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
}

/** 批次录入中的单箱信息 */
export interface CreateCartonItemInBatch {
  cartonId: string;
  products?: CartonProductItemDTO[];
}

/** 批次录入货物请求 */
export interface CreateCartonBatchRequest {
  batchId: string;
  boxCount: number;
  fromSupplierId: string;
  belongClientId: string;
  batchRemark?: string;
  cartons: CreateCartonItemInBatch[];
}

/** 更新货物请求（详情编辑；不含运单号，运单请用 bindShipment） */
export interface UpdateCartonRequest {
  cartonId?: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
  measuredWeightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  billingWeightKg?: number;
}

/** 批量更新供应商 */
export interface BatchUpdateSupplierRequest {
  cartonIds: string[];
  fromSupplierId: string;
}

/** 将多箱绑定到运单 */
export interface BindCartonsToShipmentRequest {
  cartonIds: string[];
  shipmentId: string;
}

/** 收货请求：到达方式、查询单号、照片（标注 -> base64） */
export interface ReceiveCartonRequest {
  arrivedBy: number; // 0 货拉拉，1 快递
  queryParamOfShippment?: string;
  pictures?: Record<string, string>;
}

/** 批量收货请求 */
export interface ReceiveCartonBatchRequest {
  cartonIds: string[];
  arrivedBy: number;
  queryParamOfShippment?: string;
  pictures?: Record<string, string>;
}

export const cartonApi = {
  list: (params?: {
    shipmentId?: string;
    belongClientId?: string | string[];
    forBind?: boolean;
    bindShipmentId?: string;
    cartonIdKeyword?: string;
    shipmentIdKeyword?: string;
    page?: number;
    size?: number;
  }) =>
    kfcApi.get('/cartons', { params: params ?? {} }) as Promise<
      AxiosResponse<CartonDTO[] | { content: CartonDTO[]; totalElements: number }>
    >,
  /** 解析 list 响应：无 page 时为数组，有 page 时为 Spring Page */
  unwrapList: (data: CartonDTO[] | { content: CartonDTO[]; totalElements: number } | undefined): CartonDTO[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.content || [];
  },
  get: (id: string) => kfcApi.get(`/cartons/${id}`) as Promise<AxiosResponse<CartonDTO>>,
  createCartons: (body: { cartons: CreateCartonRequest[] }) =>
    kfcApi.post('/cartons', body) as Promise<AxiosResponse<CartonDTO[]>>,
  createCartonBatch: (body: CreateCartonBatchRequest) =>
    kfcApi.post('/cartons/batch', body) as Promise<AxiosResponse<CartonDTO[]>>,
  update: (id: string, body: UpdateCartonRequest) =>
    kfcApi.put(`/cartons/${id}`, body) as Promise<AxiosResponse<CartonDTO>>,
  batchUpdateSupplier: (body: BatchUpdateSupplierRequest) =>
    kfcApi.post('/cartons/batch-update-supplier', body) as Promise<AxiosResponse<CartonDTO[]>>,
  bindShipment: (body: BindCartonsToShipmentRequest) =>
    kfcApi.post('/cartons/bind-shipment', body) as Promise<AxiosResponse<CartonDTO[]>>,
  receive: (id: string, body: ReceiveCartonRequest) =>
    kfcApi.post(`/cartons/${id}/receive`, body) as Promise<AxiosResponse<CartonDTO>>,
  receiveBatch: (body: ReceiveCartonBatchRequest) =>
    kfcApi.post('/cartons/receive', body) as Promise<AxiosResponse<CartonDTO[]>>,
};

/** 操作日志 */
export interface OperationLogDTO {
  id?: string;
  loginName?: string;
  identityId?: string;
  displayName?: string;
  operatedAt?: string;
  module?: string;
  resourceType?: string;
  resourceId?: string;
  action?: string;
  actionLabel?: string;
  summary?: string;
  requestMethod?: string;
  requestPath?: string;
  result?: string;
  errorMessage?: string;
  clientIp?: string;
  userAgent?: string;
  durationMs?: number;
  details?: Record<string, unknown>;
}

export interface OperationLogPage {
  data: OperationLogDTO[];
  page: number;
  per_page: number;
  total: number;
}

export const operationLogApi = {
  list: (params?: {
    page?: number;
    perPage?: number;
    loginName?: string;
    module?: string;
    action?: string;
    result?: string;
    startTime?: string;
    endTime?: string;
    resourceType?: string;
    resourceId?: string;
  }) =>
    kfcApi.get('/operation-logs', { params: params ?? {} }) as Promise<AxiosResponse<OperationLogPage>>,
};

export interface DashboardMetricsDTO {
  sampleCount: number;
  avgDays?: number | null;
  maxDays?: number | null;
  minDays?: number | null;
  p90Days?: number | null;
}

export interface DashboardBucketCount {
  label: string;
  upperBound?: number | null;
  count: number;
}

export interface DashboardStatusSection {
  status: string;
  statusLabel: string;
  priority: number;
  totalCount: number;
  alertCount: number;
  thresholdDays?: number | null;
  buckets: DashboardBucketCount[];
}

export interface DashboardDwellDTO {
  sections: DashboardStatusSection[];
}

export interface DashboardShipmentRowDTO {
  shipmentId: string;
  clientCode?: string;
  agentIdentifier?: string;
  status: string;
  statusChangedAt?: string;
  dwellDays: number;
  alert: boolean;
}

export const shipmentDashboardApi = {
  metrics: (params?: { from?: string; to?: string }) =>
    kfcApi.get('/shipments/dashboard/metrics', { params: params ?? {} }) as Promise<AxiosResponse<DashboardMetricsDTO>>,
  dwell: (params?: { statuses?: string }) =>
    kfcApi.get('/shipments/dashboard/dwell', { params: params ?? {} }) as Promise<AxiosResponse<DashboardDwellDTO>>,
  shipments: (params: {
    status: string;
    bucket?: number;
    openBucket?: boolean;
    page?: number;
    size?: number;
  }) =>
    kfcApi.get('/shipments/dashboard/shipments', { params }) as Promise<
      AxiosResponse<{ content: DashboardShipmentRowDTO[]; totalElements: number }>
    >,
};

export interface ShipmentSlaThresholdsDTO {
  thresholdsByStatus: Record<string, number>;
}

export const shipmentSlaThresholdApi = {
  get: () =>
    kfcApi.get('/system/shipment-sla-thresholds') as Promise<AxiosResponse<ShipmentSlaThresholdsDTO>>,
  put: (dto: ShipmentSlaThresholdsDTO) =>
    kfcApi.put('/system/shipment-sla-thresholds', dto) as Promise<AxiosResponse<ShipmentSlaThresholdsDTO>>,
};
